import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isBlank } from '@ember/utils';
import AbstractCondition from './abstract';
import { addError } from './abstract';
import { all } from 'cio/app-models/conditions/group';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import RefinementCondition from './refinement';
import { FormType } from 'cio/utils/forms';

class FormCondition extends AbstractCondition {
  // Form conditions are processed on the back-end exactly like
  // event conditions for "form_submit" event and with specific "form_id" filter
  // but they're presented differently in UI:
  type = 'event';
  uiType = 'form';

  trackedAttributes = ['inverse', 'filters', 'formFilter'];

  @computed('refinement', 'filters.[]')
  get children() {
    let { refinement, filters } = this;

    return [refinement, ...filters] as AbstractCondition[];
  }

  @computed('shouldValidate', 'formID')
  get errors() {
    let errors: Record<string, string[]> = {};

    if (this.shouldValidate) {
      if (isBlank(this.formID)) {
        addError(errors, 'form', "can't be blank");
      }
    }

    return errors;
  }

  name = 'form_submit';
  inverse = false;
  refinement: RefinementCondition | null = null;

  formFilter!: FormEventAttributeCondition;
  filters: EventAttributeCondition[] = [];
  filterOperator: 'and' | 'or' = all;

  @alias('formFilter.value') formID!: string;
  @alias('formFilter.formType') formType!: FormType;

  setForm(formType: string, formID: string) {
    this.formFilter.setProperties({
      formType,
      value: formID,
    });
  }

  init() {
    super.init();

    if (!this.formFilter) {
      this.set(
        'formFilter',
        FormEventAttributeCondition.create({
          value: '',
          formType: FormType.FacebookLeadAds,
        }),
      );
    }
  }

  @action
  duplicate() {
    let duplicate = FormCondition.create();
    this.copyAllAttributes(this, duplicate);
    return duplicate;
  }

  copyAllAttributes(source: FormCondition, target: FormCondition) {
    target.setProperties({
      _event: this.event,
      name: source.name,
      uiType: source.uiType,
      inverse: source.inverse,
      filterOperator: source.filterOperator,
      refinement: source.refinement?.duplicate() ?? null,
      filters: this.copyFilters(source.filters),
      formFilter: source.formFilter.duplicate(),
    });
  }

  private copyFilters(originalFilters: EventAttributeCondition[]) {
    let filters = [];
    for (let i = 0; i < originalFilters.length; i++) {
      let filter = originalFilters[i].duplicate();
      filters.push(filter);
    }
    return filters;
  }

  toString() {
    return 'FormCondition';
  }
}

export default FormCondition;
